// Using any for now to avoid circular dependencies - can be refined later
interface NotificationMetadata {
  project_id?: string;
  project_type?: string;
  pipeline_id?: string;
  ticket_id?: string;
  organization_id?: string;
  meeting_link?: string;
  [key: string]: any;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  metadata?: NotificationMetadata;
  link?: string;
  created_at: string;
  read: boolean;
}

export interface NotificationLink {
  url: string;
  external?: boolean;
}

/**
 * Generates the appropriate navigation link for a notification
 * @param notification - The notification object
 * @returns NotificationLink object with url and external flag, or null if no link should be generated
 */
export function generateNotificationLink(notification: Notification): NotificationLink | null {
  if (!notification) return null;

  const metadata = notification.metadata || {};
  
  // If notification already has a link, use it
  if (notification.link) {
    return {
      url: notification.link,
      external: notification.link.startsWith('http')
    };
  }

  // If metadata has a link, use it
  if (metadata.link) {
    return {
      url: metadata.link,
      external: metadata.link.startsWith('http')
    };
  }

  // Generate links based on notification type
  switch (notification.type) {
    // Project-related notifications
    case 'project_created':
    case 'project_assigned':
    case 'project_stage_move':
    case 'project_update':
    case 'project_follower':
      if (metadata.project_id) {
        const projectType = metadata.project_type?.toLowerCase();
        const isCommercial = projectType?.includes('com') || projectType?.includes('commercial');
        const pipelineRoute = isCommercial ? 'pipeline' : 'pipeline-resi';
        let finalUrl = `/app/${pipelineRoute}?projectId=${metadata.project_id}&modal=true`;
        
        // Add pipeline ID if available
        if (metadata.pipeline_id) {
          finalUrl = `/app/${pipelineRoute}?pipelineId=${metadata.pipeline_id}&projectId=${metadata.project_id}&modal=true`;
        }
        
        return { url: finalUrl };
      }
      break;

    // Comment-related notifications  
    case 'project_comment':
    case 'project_mention':
    case 'comment':
    case 'mention':
      if (metadata.project_id) {
        const projectType = metadata.project_type?.toLowerCase();
        const isCommercial = projectType?.includes('com') || projectType?.includes('commercial');
        const pipelineRoute = isCommercial ? 'pipeline' : 'pipeline-resi';
        let finalUrl = `/app/${pipelineRoute}?projectId=${metadata.project_id}&modal=true&tab=communication`;
        
        if (metadata.pipeline_id) {
          finalUrl = `/app/${pipelineRoute}?pipelineId=${metadata.pipeline_id}&projectId=${metadata.project_id}&modal=true&tab=communication`;
        }
        
        return { url: finalUrl };
      }
      break;

    // Task-related notifications
    case 'task_assigned':
    case 'task_updated':
    case 'task_completed':
    case 'task_reopened':
    case 'task_comment':
    case 'task_created':
    case 'task_due':
      if (metadata.project_id) {
        const projectType = metadata.project_type?.toLowerCase();
        const isCommercial = projectType?.includes('com') || projectType?.includes('commercial');
        const pipelineRoute = isCommercial ? 'pipeline' : 'pipeline-resi';
        let finalUrl = `/app/${pipelineRoute}?projectId=${metadata.project_id}&modal=true&tab=tasks`;

        if (metadata.pipeline_id) {
          finalUrl = `/app/${pipelineRoute}?pipelineId=${metadata.pipeline_id}&projectId=${metadata.project_id}&modal=true&tab=tasks`;
        }

        return { url: finalUrl };
      }
      break;

    // Ticket-related notifications
    case 'ticket_created':
    case 'ticket_updated':
    case 'ticket_comment':
    case 'ticket_assigned':
    case 'ticket_resolved':
    case 'ticket':
      if (metadata.ticket_id) {
        return { url: `/app/support-tickets/${metadata.ticket_id}` };
      }
      return { url: '/app/support-tickets' };

    // Organization-related notifications
    case 'organization_created':
    case 'organization_updated':
    case 'organization_comment':
    case 'organization_follower':
      if (metadata.organization_id) {
        return { url: `/app/organizations/${metadata.organization_id}` };
      }
      break;

    // Booking-related notifications
    case 'booking_reminder':
    case 'booking_created':
    case 'booking_updated':
    case 'booking_cancelled':
    case 'booking_reminder_1hr_host':
    case 'booking_reminder_1hr_attendee':
    case 'booking_reminder_5min_host':
    case 'booking_reminder_5min_attendee':
    case 'booking_scheduled_host':
    case 'booking_scheduled_attendee':
      // Use meeting link if available, otherwise default to scheduler
      if (metadata.meeting_link) {
        return { 
          url: metadata.meeting_link,
          external: true
        };
      }
      return { 
        url: 'https://go.solerro.com/app/scheduler/appointments',
        external: true
      };

    // Tag notifications (mentions)
    case 'tag':
      if (metadata.project_id && metadata.project_name) {
        const projectType = metadata.project_type?.toLowerCase();
        const isCommercial = projectType?.includes('com') || projectType?.includes('commercial');
        const pipelineRoute = isCommercial ? 'pipeline' : 'pipeline-resi';
        let finalUrl = `/app/${pipelineRoute}?projectId=${metadata.project_id}&modal=true&tab=communication`;
        
        if (metadata.pipeline_id) {
          finalUrl = `/app/${pipelineRoute}?pipelineId=${metadata.pipeline_id}&projectId=${metadata.project_id}&modal=true&tab=communication`;
        }
        
        return { url: finalUrl };
      }
      break;

    // System notifications - typically no specific link
    case 'system':
    case 'user_welcome':
      return null;

    default:
      // For unknown notification types, check if there's project context
      if (metadata.project_id) {
        const projectType = metadata.project_type?.toLowerCase();
        const isCommercial = projectType?.includes('com') || projectType?.includes('commercial');
        const pipelineRoute = isCommercial ? 'pipeline' : 'pipeline-resi';
        let finalUrl = `/app/${pipelineRoute}?projectId=${metadata.project_id}&modal=true`;
        
        if (metadata.pipeline_id) {
          finalUrl = `/app/${pipelineRoute}?pipelineId=${metadata.pipeline_id}&projectId=${metadata.project_id}&modal=true`;
        }
        
        return { url: finalUrl };
      }
      break;
  }

  return null;
}

/**
 * Formats a notification link for email templates
 * @param notification - The notification object  
 * @param baseUrl - The base URL for the application (e.g., https://go.solerro.com)
 * @returns Formatted URL for email, or null if no link available
 */
export function generateEmailNotificationLink(notification: Notification, baseUrl: string): string | null {
  const linkObj = generateNotificationLink(notification);
  if (!linkObj) return null;
  
  // If it's already an external link, return as-is
  if (linkObj.external) {
    return linkObj.url;
  }
  
  // Combine with base URL for internal links
  return `${baseUrl.replace(/\/$/, '')}${linkObj.url}`;
}

/**
 * Formats a notification link for Slack with proper formatting
 * @param notification - The notification object
 * @param baseUrl - The base URL for the application  
 * @returns Slack-formatted link text, or null if no link available
 */
export function generateSlackNotificationLink(notification: Notification, baseUrl: string): string | null {
  const linkObj = generateNotificationLink(notification);
  if (!linkObj) return null;
  
  let fullUrl: string;
  if (linkObj.external) {
    fullUrl = linkObj.url;
  } else {
    fullUrl = `${baseUrl.replace(/\/$/, '')}${linkObj.url}`;
  }
  
  // Determine link text based on notification type
  let linkText = 'View Details';
  
  switch (notification.type) {
    case 'project_created':
    case 'project_assigned':
    case 'project_stage_move':
    case 'project_update':
    case 'project_follower':
      linkText = 'View Project';
      break;
    case 'project_comment':
    case 'project_mention':
    case 'comment':
    case 'mention':
    case 'tag':
      linkText = 'View Comment';
      break;
    case 'task_assigned':
    case 'task_updated':
    case 'task_completed':
    case 'task_reopened':
    case 'task_comment':
    case 'task_created':
      linkText = 'View Task';
      break;
    case 'ticket_created':
    case 'ticket_updated':
    case 'ticket_comment':
    case 'ticket':
      linkText = 'View Ticket';
      break;
    case 'booking_reminder':
    case 'booking_created':
      linkText = 'Join Meeting';
      break;
  }
  
  return `<${fullUrl}|${linkText}>`;
}